import { useLazyQuery } from "@apollo/client";
import { Alert, Collapse, DialogContentText } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { ConfirmDialog } from "components/ConfirmDialog";
import { useBulkDeleteSubmissions } from "hooks/useSubmissionMutations";
import pluralize from "pluralize";
import { SUBMISSION_REVISION_CHECK_QUERY } from "queries/submissions";
import { useEffect, useMemo } from "react";

import { Row } from "./columns";
import { partitionSubmissions } from "./utils";

type DeleteSubmissionsDialogProps = {
  open: boolean;
  onClose: () => void;
  submissions: Row[];
  onSuccess: () => void;
};

export function DeleteSubmissionsDialog({
  open,
  onClose,
  submissions,
  onSuccess,
}: DeleteSubmissionsDialogProps) {
  const alerts = useAlerts();
  const [bulkDeleteSubmissions, { loading }] = useBulkDeleteSubmissions();
  const [checkRevisions, { data: revisionData, loading: revisionCheckLoading }] = useLazyQuery(
    SUBMISSION_REVISION_CHECK_QUERY
  );

  const { uniqueSubmissionIds } = useMemo(() => partitionSubmissions(submissions), [submissions]);

  useEffect(() => {
    if (open && uniqueSubmissionIds.length > 0) {
      checkRevisions({
        variables: { submissionIds: uniqueSubmissionIds },
      });
    }
  }, [open, uniqueSubmissionIds, checkRevisions]);

  const submissionsWithRevisions = useMemo(
    () =>
      revisionData?.getRevisionSubmissions?.filter((s) => s.revisionSubmissions.length > 0) ?? [],
    [revisionData]
  );

  const hasRevisions = submissionsWithRevisions.length > 0;

  const handleConfirm = async () => {
    try {
      await bulkDeleteSubmissions({
        variables: {
          input: {
            submissionIds: uniqueSubmissionIds,
          },
        },
        refetchQueries: ["WasteSubmissionsForStaff", "WasteSubmissionsStaffSummary"],
        awaitRefetchQueries: true,
      });

      alerts.success("Submission(s) deleted successfully");
      onSuccess();
      onClose();
    } catch (error) {
      alerts.error("Failed to delete submission(s)", error);
    }
  };

  return (
    <ConfirmDialog
      open={open}
      loading={loading}
      onClose={onClose}
      header="Delete Submissions"
      disableConfirm={hasRevisions || revisionCheckLoading}
      onConfirm={handleConfirm}
    >
      <DialogContentText>
        {`Are you sure you want to delete the selected ${
          submissions.length > 1 ? submissions.length : ""
        } ${pluralize(
          "submission",
          submissions.length
        )}? Any submission documents will also be deleted.`}
      </DialogContentText>
      <Alert severity="warning" sx={{ mt: 2 }}>
        Deleting a submission that is part of a recurring schedule does not remove its occurrence —
        it will reappear as a Not Started submission. To remove occurrences from the schedule, use
        &ldquo;Exclude from Schedule...&rdquo; instead.
      </Alert>
      <Collapse in={hasRevisions} sx={{ mt: 2 }}>
        <Alert severity="error">
          {submissionsWithRevisions.length === 1
            ? `"${submissionsWithRevisions[0].title ?? "Untitled Submission"}" has linked revision submissions and cannot be deleted. Please delete the revision submissions first.`
            : `${submissionsWithRevisions.length} of the selected submissions have linked revision submissions and cannot be deleted. Please delete the revision submissions first.`}
        </Alert>
      </Collapse>
    </ConfirmDialog>
  );
}
